import { cn } from "../lib/cn";

export interface TabItem<T extends string> {
  value: T;
  label: string;
  /** Renders the tab but refuses selection, for sections not built yet. */
  disabled?: boolean;
}

export interface TabsProps<T extends string> {
  value: T;
  items: readonly TabItem<T>[];
  onChange: (value: T) => void;
  label: string;
  className?: string;
}

/**
 * The tab strip on the bird detail screen — a scrolling row of pills, not an underline.
 *
 * Scrolls horizontally on a phone rather than wrapping: a second row of tabs pushes the
 * record itself below the fold. The selected pill inverts to the ink colour, which is
 * the strongest contrast on the screen and the only place the design uses it as a fill.
 */
export function Tabs<T extends string>({ value, items, onChange, label, className }: TabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        "flex gap-2 overflow-x-auto",
        "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {items.map((item) => {
        const selected = item.value === value;

        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            className={cn(
              "inline-flex h-pill shrink-0 items-center rounded-control px-4",
              "text-pill transition-colors",
              "disabled:cursor-not-allowed disabled:opacity-45",
              selected
                ? "bg-on-surface text-surface"
                : "bg-surface-container text-on-surface-body hover:bg-surface-container-high",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
